package ui.app.assign.detail.wheel.editor

import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.material3.Button
import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalFontFamilyResolver
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import api.ResourceType
import apps.App
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.ScreenLand
import compose.assets.svg.ScreenPort
import io.github.alexzhirkevich.qrose.QrCodePainter
import kotlinx.coroutines.*
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.plus
import org.jetbrains.compose.resources.StringResource
import org.ssoft.common.tools.nowDate
import provider.CommonDisplayProvider
import provider.LocalAccountProvider
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.globalViewModel
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.*
import ui.GlobalVM
import ui.app.assign.detail.AppDetailArgs
import ui.app.assign.detail.AppWrapperDevices
import ui.app.assign.detail.wheel.AppWheelScope
import ui.app.assign.detail.wheel.editor.pages.*
import ui.snackBarStateGlobal
import ui.widget.DropDownResItem
import kotlin.time.Clock
import kotlin.time.Duration.Companion.minutes


class AppEditorWheelScope private constructor(
    val vm: AppEditorWheelVM,
    val vmGlobal: GlobalVM,
    val action: (AppEditorWheelVM.Action) -> Unit,

    val args: AppDetailArgs,
    val nav: Navigation,
    val login: Io.User,
    val display: CommonDisplayProvider,
    val textResolver: FontFamily.Resolver
) : AppWheelScope {

    companion object {
        @Composable
        fun rememberScope(): AppEditorWheelScope {
            val nav = LocalNavigation.current
            val account = LocalAccountProvider.current
            val display = LocalDisplayProvider.current
            val resolver = LocalFontFamilyResolver.current
            val args = LocalNavigationArgs.current.rememberArgs(AppDetailArgs::class)
            val vm = nav.viewModel(AppEditorWheelVM::class, "${args.companyId}:${args.appId}")
            val vmGlobal = globalViewModel(GlobalVM::class)
            val action = AppEditorWheelVM.Action.rememberAction(vm)
            return remember {
                AppEditorWheelScope(
                    vm = vm,
                    vmGlobal = vmGlobal,
                    action = action,
                    args = args,
                    nav = nav,
                    login = account.current!!,
                    display = display,
                    textResolver = resolver
                )
            }
        }
    }

    class ScopeButtonNext(private val scope: CoroutineScope) {
        fun showMessage(msg: StringResource): Boolean {
            scope.launch {
                snackBarStateGlobal.showSnackbar(
                    message = msg.await(),
                    withDismissAction = true,
                    duration = SnackbarDuration.Long
                )
            }
            return false
        }

        companion object {

            @Composable
            fun remember(): ScopeButtonNext {
                val scope = rememberCoroutineScope()
                return remember {
                    ScopeButtonNext(scope)
                }
            }
        }
    }

    @Composable
    fun ButtonNext(
        modifier: Modifier = Modifier,
        validate: ScopeButtonNext.() -> Boolean = { true }
    ) {
        val btnScope = ScopeButtonNext.remember()
        Button(
            onClick = {
                with(btnScope) {
                    if (validate()) vm.activePageIndex += 1
                }
            },
            modifier = modifier.width(256.dp)
        ) {
            Text(
                text = Res.string.dialog__action_next.rememberString(),
                fontWeight = FontWeight.Black,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }

    class ThemeWrapper(
        backgroundStartupLand: Long = -1,
        backgroundStartupPort: Long = -2,
        backgroundAppLand: Long = -1,
        backgroundAppPort: Long = -2,
        background: Long,
        surface: Long,
        onSurface: Long
    ) {
        operator fun get(orientation: Int): App.Wheel.Theme {
            return if (orientation == App.Wheel.Theme.ORIENTATION_LAND) land
            else port
        }

        val land = App.Wheel.Theme(
            backgroundApp = backgroundAppLand,
            backgroundStartup = backgroundStartupLand,
            backgroundColor = background,
            items = App.Color(
                surface = surface,
                onSurface = onSurface,
            )
        )

        val port = App.Wheel.Theme(
            backgroundApp = backgroundAppPort,
            backgroundStartup = backgroundStartupPort,
            backgroundColor = background,
            items = App.Color(
                surface = surface,
                onSurface = onSurface,
            )
        )

        companion object {
            fun findStartup(id: Long, orirntation: Int): Long? {
                return all.takeIf { id < 0 }
                    ?.firstOrNull { it.land.backgroundStartup == id || it.port.backgroundStartup == id }
                    ?.let {
                        when {
                            orirntation == App.Wheel.Theme.ORIENTATION_LAND
                                    && it.port.backgroundStartup == id -> it.land.backgroundStartup

                            orirntation == App.Wheel.Theme.ORIENTATION_PORT
                                    && it.land.backgroundStartup == id -> it.port.backgroundStartup

                            else -> null
                        }
                    }
            }

            val all by lazy {
                listOf(
                    //FF47A8BD
                    ThemeWrapper(
                        backgroundStartupLand = -36,
                        backgroundStartupPort = -37,
                        backgroundAppLand = -1,
                        backgroundAppPort = -2,
                        background = 0xFF47A8BD,
                        surface = 0xFF3f51b5,
                        onSurface = 0xFFffFFff
                    ),
                    //FE938C
                    ThemeWrapper(
                        backgroundStartupLand = -36,
                        backgroundStartupPort = -37,
                        backgroundAppLand = -1,
                        backgroundAppPort = -2,
                        background = 0xFFFE938C,
                        surface = 0xFF673ab7,
                        onSurface = 0xFFffFFff
                    ),
                    //E88D67
                    ThemeWrapper(
                        backgroundStartupLand = -36,
                        backgroundStartupPort = -37,
                        backgroundAppLand = -1,
                        backgroundAppPort = -2,
                        background = 0xFFE88D67,
                        surface = 0xFF2196f3,
                        onSurface = 0xFFffFFff
                    ),
                    // D68FD6
                    ThemeWrapper(
                        backgroundStartupLand = -36,
                        backgroundStartupPort = -37,
                        backgroundAppLand = -1,
                        backgroundAppPort = -2,
                        background = 0xFFD68FD6,
                        surface = 0xFF009688,
                        onSurface = 0xFFffFFff
                    ),
                    // 3A405A
                    ThemeWrapper(
                        backgroundStartupLand = -36,
                        backgroundStartupPort = -37,
                        backgroundAppLand = -1,
                        backgroundAppPort = -2,
                        background = 0xFF3A405A,
                        surface = 0xFF4caf50,
                        onSurface = 0xFFffFFff
                    ),
                    // 062726
                    ThemeWrapper(
                        backgroundStartupLand = -36,
                        backgroundStartupPort = -37,
                        backgroundAppLand = -1,
                        backgroundAppPort = -2,
                        background = 0xFF062726,
                        surface = 0xFF607d8b,
                        onSurface = 0xFFffFFff
                    ),
                    // C7CEDB
                    ThemeWrapper(
                        backgroundStartupLand = -36,
                        backgroundStartupPort = -37,
                        backgroundAppLand = -1,
                        backgroundAppPort = -2,
                        background = 0xFFC7CEDB,
                        surface = 0xFFcddc39,
                        onSurface = 0xFF4A4F15
                    ),
                    // B3D2B2
                    ThemeWrapper(
                        backgroundStartupLand = -36,
                        backgroundStartupPort = -37,
                        backgroundAppLand = -1,
                        backgroundAppPort = -2,
                        background = 0xFFB3D2B2,
                        surface = 0xFFffc107,
                        onSurface = 0xFF423202
                    ),
                    //192A51
                    ThemeWrapper(
                        backgroundStartupLand = -36,
                        backgroundStartupPort = -37,
                        backgroundAppLand = -1,
                        backgroundAppPort = -2,
                        background = 0xFF192A51,
                        surface = 0xFF795548,
                        onSurface = 0xFFffFFff
                    ),
                )
            }
        }
    }

    sealed class ImagePickerVariants(
        override val label: StringResource,
        override val image: ImageVector? = null
    ) : DropDownResItem {
        companion object {
            data object None : ImagePickerVariants(Res.string.layer_bottom_wheel_page_items_icon_none)
            data object Icon : ImagePickerVariants(Res.string.layer_bottom_wheel_page_items_icon_svg)
            data object Image : ImagePickerVariants(Res.string.layer_bottom_wheel_page_items_icon_webp)

            val all by lazy {
                listOf(
                    None, Icon, Image
                )
            }
        }
    }

    data class AppOrientation(
        /**
         * @see App.Wheel.Theme.ORIENTATION_LAND
         * @see App.Wheel.Theme.ORIENTATION_PORT
         */
        val orientation: Int,
        override val label: StringResource,
        override val image: ImageVector?
    ) : DropDownResItem {
        companion object {
            val all by lazy {
                listOf(
                    AppOrientation(
                        App.Wheel.Theme.ORIENTATION_PORT,
                        label = Res.string.layer_bottom_wheel_page_info_orientation_port,
                        image = Svg.ScreenPort,

                        ),
                    AppOrientation(
                        App.Wheel.Theme.ORIENTATION_LAND,
                        label = Res.string.layer_bottom_wheel_page_info_orientation_land,
                        image = Svg.ScreenLand
                    )
                )
            }
        }
    }

    data class Page(
        val title: StringResource,
        val body: @Composable AppEditorWheelScope.(nnvl: Wrapper) -> Unit
    ) {
        companion object {
            val all by lazy {
                listOf(
                    Page(Res.string.layer_bottom_wheel_page_info) {
                        AppWheelPage01_Info(it)
                    },
                    Page(Res.string.layer_bottom_wheel_page_theme) {
                        AppWheelPage02_Theme(it)
                    },
                    Page(Res.string.layer_bottom_wheel_page_items) {
                        AppWheelPage03_Items(it)
                    },
                    Page(Res.string.layer_bottom_wheel_page_activation) {
                        AppWheelPage04_Qr(it)
                    },
                    Page(Res.string.layer_bottom_wheel_page_summary) {
                        AppWheelPage05_Summary(it)
                    })
            }
        }
    }


    init {
        vm.init(this)
    }

    class Wrapper(
        val canEdit: Boolean,
        val canDelete: Boolean,
        override val data: Io.AppDetail,
        override val key: String = data.id.toString(),
        private val changeInternal: MutableState<Boolean> = mutableStateOf(false),
        val onRedrawChange: (Wrapper) -> Unit
    ) : EFieldList.Entry<Io.AppDetail> {


        val appRef: App.Wheel get() = data.data as App.Wheel

        val change by changeInternal

        val items = ItemsWrapper(canEdit, appRef.content.items) {
            onRedrawChange(this)
        }

        val devices = AppWrapperDevices(data)

        val qrCodePainter = QrCodePainter("https://game.screensoft.org/${data.type}/${data.id}")

        val name = EFieldTextString(
            property = data::name,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = {
                makeChanges()
            }
        )

        val logo = EFieldRegularLong(
            property = data::logo,
            onChange = { makeChanges() }
        )


        val note = EFieldTextString(
            property = data::note,
            onChange = {
                makeChanges()
            }
        )


        val start = EFieldRegularDate(
            property = data::start,
            onChange = {
                makeChanges()
            }
        )

        val end = EFieldRegularDate(
            property = data::end,
            onChange = {
                makeChanges()
            }
        )

        val scheduled = EFieldRegularBoolean(
            property = data::scheduled,
            onChange = {
                if (it) {
                    start.set(Clock.nowDate())
                    end.set(Clock.nowDate().plus(1, DateTimeUnit.MONTH))
                }
                makeChanges()
            }
        )

        val orientation = EFieldRegularInt(
            property = appRef.content.theme::orientation,
            onChange = {
                ThemeWrapper.findStartup(themeBgStart.state, it)?.also {
                    themeBgStart.set(it)
                }
                makeChanges()
            }
        )

        val themeBgColor = EFieldColorAsLong(
            property = appRef.content.theme::backgroundColor,
            onChange = {
                makeChanges()
            }
        )

        val themeBgStart = EFieldRegularLong(
            property = appRef.content.theme::backgroundStartup,
            onChange = { makeChanges() }
        )

        val themeBgGame = EFieldRegularLong(
            property = appRef.content.theme::backgroundApp,
            onChange = { makeChanges() }
        )

        val themeItemColorBg = EFieldColorAsLong(
            property = appRef.content.theme.items::surface,
            onChange = { newColor ->
                makeChanges()
                items.state.forEach {
                    it.colorBg.set(newColor)
                }
            }
        )

        val themeItemColorFg = EFieldColorAsLong(
            property = appRef.content.theme.items::onSurface,
            onChange = { newColor ->
                makeChanges()
                items.state.forEach {
                    it.colorFg.set(newColor)
                }
            }
        )

        val themeItemFontSize = EFieldDualIntAsFloat(
            property = appRef.content.theme::itemsFontSize,
            onChange = { newFont ->
                makeChanges()
                items.state.forEach {
                    it.fontSize.set(newFont)
                }
            }
        )
        val themeItemIconScale = EFieldDualIntAsFloat(
            property = appRef.content.theme::iconsScale,
            onChange = { newFont ->
                makeChanges()
                items.state.forEach {
                    it.iconScale.set(newFont)
                }
            }
        )
        val themeItemIconY = EFieldDualIntAsFloat(
            property = appRef.content.theme::iconsY,
            onChange = { newFont ->
                makeChanges()
                items.state.forEach {
                    it.iconY.set(newFont)
                }
            }
        )

        val themeWheelPosX = EFieldDualIntAsFloat(
            property = appRef.content.wheel::x,
            onChange = {
                makeChanges()
            }
        )

        val themeWheelPosY = EFieldDualIntAsFloat(
            property = appRef.content.wheel::y,
            onChange = {
                makeChanges()
            }
        )

        val themeWheelScale = EFieldDualIntAsFloat(
            property = appRef.content.wheel::scale,
            onChange = {
                makeChanges()
            }
        )

        val themeItemNameY = EFieldDualIntAsFloat(
            property = appRef.content.theme::itemNameY,
            onChange = { newY ->
                makeChanges()
                items.state.forEach {
                    it.nameY.set(newY)
                }
            }
        )


        val themeBrandingScaleCircle = EFieldDualIntAsFloat(
            property = appRef.content.branding::scaleCircle,
            onChange = {
                makeChanges()
            }
        )
        val themeBrandingImageShow = EFieldRegularBoolean(
            property = appRef.content.branding::showBranding,
            onChange = {
                makeChanges()
            }
        )
        val themeBrandingImageScale = EFieldDualIntAsFloat(
            property = appRef.content.branding::scaleImage,
            onChange = {
                makeChanges()
            }
        )
        val themeBrandingImage = EFieldRegularLong(
            property = appRef.content.branding::image,
            onChange = {
                makeChanges()
            }
        )
        val themeBrandingBackground = EFieldColorAsLong(
            property = appRef.content.branding.color::surface,
            onChange = {
                makeChanges()
            }
        )

        val qrPosX = EFieldDualIntAsFloat(
            property = appRef.content.qr::x,
            onChange = {
                makeChanges()
            }
        )

        val qrPosY = EFieldDualIntAsFloat(
            property = appRef.content.qr::y,
            onChange = {
                makeChanges()
            }
        )

        val qrScale = EFieldDualIntAsFloat(
            property = appRef.content.qr::scale,
            onChange = {
                makeChanges()
            }
        )

        val timerSplash = EFieldDualLongAsFloat(
            property = appRef.content.timer::splash,
            maxValue = 2.minutes.inWholeMilliseconds,
            onChange = {
                makeChanges()
            }
        )
        val timerGame = EFieldDualLongAsFloat(
            property = appRef.content.timer::game,
            maxValue = 2.minutes.inWholeMilliseconds,
            onChange = {
                makeChanges()
            }
        )

        val timerResult = EFieldDualLongAsFloat(
            property = appRef.content.timer::result,
            maxValue = 2.minutes.inWholeMilliseconds,
            onChange = {
                makeChanges()
            }
        )

        val qrPadding = EFieldDualIntAsFloat(
            property = appRef.content.qr::padding,
            onChange = {
                makeChanges()
            }
        )

        val activateByQr = EFieldRegularBoolean(
            property = appRef.content.qr::visible,
            onChange = {
                makeChanges()
            }
        )

        val activateByTouch = EFieldRegularBoolean(
            property = appRef.content.qr::touchable,
            onChange = {
                makeChanges()
            }
        )

        private val fieldsText = listOf(
            name,
            note
        )

        private val allFields = fieldsText + listOf(
            logo,
            start,
            end,
            scheduled,
            themeBgStart,
            themeBgGame,
            themeItemColorBg,
            themeItemColorFg,
            themeItemFontSize,
            themeWheelPosX,
            themeWheelPosY,
            themeWheelScale,
            themeItemIconScale,
            themeItemIconY,
            themeItemNameY,

            themeBrandingImageScale,
            themeBrandingScaleCircle,
            themeBrandingImageShow,
            themeBrandingImage,
            themeBrandingBackground,

            themeBgColor,

            qrPosX,
            qrPosY,
            qrScale,
            qrPadding,

            activateByQr,
            activateByTouch,

            timerSplash,
            timerGame,
            timerResult
        )

        init {
            onRedrawChange(this)
        }

        private fun makeChanges() {
            onRedrawChange(this)
            changeInternal.value =
                fieldsText.none { it.err }
                        && allFields.any { it.change }
        }

        fun applyTheme(theme: App.Wheel.Theme) {
            themeItemColorFg.set(Color(theme.items.onSurface))
            themeItemColorBg.set(Color(theme.items.surface))
            themeBgColor.set(Color(theme.backgroundColor))

            if (themeBgStart.state < 0) {
                themeBgStart.set(theme.backgroundStartup)
            }

            items.state.forEach {
                it.colorBg.set(themeItemColorBg.state)
            }
            items.state.forEach {
                it.colorFg.set(themeItemColorFg.state)
            }
            onRedrawChange(this)
        }


    }


    class ItemsWrapper(
        val canEdit: Boolean,
        listing: MutableList<App.Wheel.Item>,
        private val makeRedrawChange: () -> Unit
    ) : EFieldList<App.Wheel.Item, ItemsWrapper.Item>(
        items = listing.onEach {
            it.count = maxOf(0, it.count - it.used)
        },
        onBindEntry = { Item(it, makeRedrawChange = makeRedrawChange) }
    ) {
        private var job: Job? = null
        suspend fun addItem(listState: LazyListState) = coroutineScope {
            add(0, App.Wheel.Item.empty("", 0))
            state.forEachIndexed { index, item ->
                item.position.set(index)
            }
            makeRedrawChange()

            job?.takeIf { it.isActive }
                ?.runCatching { cancel() }

            job = launch {
                if (isActive) runCatching {
                    state.firstOrNull()?.nameFocus?.apply {
                        listState.animateScrollToItem(0)
                        requestFocus()
                    }
                }
            }
        }

        fun removeItem(item: Item) {
            removeAll { it.key == item.key }
            makeRedrawChange()
        }

        class Item(
            override val data: App.Wheel.Item,
            override val key: String = data.idGroup.toString(),
            val nameFocus: FocusRequester = FocusRequester(),
            private val makeRedrawChange: () -> Unit
        ) : Entry<App.Wheel.Item> {

            val name = EFieldTextString(
                property = data::name,
                onChange = {
                    makeChanges()
                    makeRedraw()
                })

            val colorBg = EFieldColorAsLong(
                property = data.color::surface,
                onChange = {
                    makeChanges()
                    makeRedraw()
                })

            val colorFg = EFieldColorAsLong(
                property = data.color::onSurface,
                onChange = {
                    makeChanges()
                    makeRedraw()
                })

            val fontSize = EFieldDualIntAsFloat(
                property = data::fontSize,
                onChange = {
                    makeChanges()
                    makeRedraw()
                })

            val iconScale = EFieldDualIntAsFloat(
                property = data.icon::scale,
                onChange = {
                    makeChanges()
                    makeRedraw()
                })
            val iconY = EFieldDualIntAsFloat(
                property = data.icon::y,
                onChange = {
                    makeChanges()
                    makeRedraw()
                })
            val nameY = EFieldDualIntAsFloat(
                property = data::nameY,
                onChange = {
                    makeChanges()
                    makeRedraw()
                })

            val position = EFieldRegularInt(
                property = data::position,
                onChange = { makeChanges() }
            )

            val counter = EFieldTextInt(
                property = data::count,
                condition = { it.trim().toIntOrNull()?.takeIf { it > 0 } },
                onChange = { makeChanges() }
            )

            val iconSvg = EFieldRegularLong(
                property = data.icon::svg,
                onChange = {
                    makeChanges()
                    makeRedraw()
                })
            val iconImage = EFieldRegularLong(
                property = data.icon::webp,
                onChange = {
                    makeChanges()
                    makeRedraw()
                })

            val reward = EFieldRegularLong(
                property = data.reward::visual,
                onChange = {
                    makeChanges()
                    makeRedraw()
                }
            )

            override fun onPositionChanged(newPosition: Int) {
                position.set(newPosition)
                makeRedraw()
            }

            private fun makeChanges() {

//                    TODO("Not yet implemented")
            }

            private fun makeRedraw() {
                makeRedrawChange()
            }

            fun resetIcon() {
                iconSvg.set(ResourceType.NO_ICON)
                iconImage.set(ResourceType.NO_IMAGE)
            }
        }
    }
}